import { Vehicle, Org, ClientHistory, go } from '../../../models'
import moment from 'moment'
import utils from 'oderon-commons/src/utils.js'

export {
  postOrPatch,
  splitAndSort,
  fetchVehiclesOfInterest,
  findActiveAssoc,
  findActiveInstall,
  findLastInstall,
  canEdit
}

async function postOrPatch(pathTemplate, body) {
  let cid = body.clientId
  let rowId = body.id
  let path = pathTemplate.replaceAll(':cid', cid)

  if (rowId == null) {
    return await go.post(path, body)
  } else {
    path = path + '/' + rowId
    return await go.patch(path, body)
  }
}

// Dada uma lista de objetos (e.g. Depots, Vehicles, Technicians, etc)
// produz uma lista de objetos que podem ser colocados em um OSelect
// ou OAutocomplete (com .text e .value), separados em "ativos" e
// "inativos" (que estão .deletedAt), com um separador entre eles.
function splitAndSort(items, { prefixToRemove } = {}) {
  let sanitize = (x) => {
    if (prefixToRemove != null) x = x.replaceAll(prefixToRemove, '')
    return x.trim()
  }

  for (let item of items) {
    item.text = sanitize(item.label || item.name || item.code)
    item.value = item.id
  }

  let byText = (a, b) => a.text.localeCompare(b.text)

  let ok = items.filter((x) => x.deletedAt == null).sort(byText)
  let deleted = items.filter((x) => x.deletedAt != null).sort(byText)

  for (let item of deleted)
    item.chips = [{ text: 'Desativado', color: 'error' }]

  return [...ok, { divider: true }, ...deleted]
}

// Dado um módulo, busca os veículos que devem ser mostrados como
// opções do OAutocomplete nas diversas popups de registro de
// histórico ou operations.
async function fetchVehiclesOfInterest(clientId) {
  // Busca os históricos de interesse (registro mais recente de
  // assoc/start ou assoc/end para cada veículo que um dia teve
  // a ver com este módulo).
  //
  // NB: Por enquanto estamos consultando as ASSOCIATIONS e não
  // as INSTALLATIONS!
  let vehiclesOfInterest = await fetchAssocsOfInterest(clientId)

  let allVehicles = await Vehicle.findMini()
  let orgs = await Org.findAll()

  // Primeiro, os veículos de interesse (um por registro
  // de assoc acima)
  let items = []

  // if (vehiclesOfInterest.length)
  items.push({ header: 'Veículos de interesse' })

  for (let hist of vehiclesOfInterest) {
    let { id, code, extraCode, orgId } = hist.vehicle

    let chips = []

    let org = orgs.find((y) => y.id == orgId)
    if (org) chips.push({ text: org.name })

    let t = moment(hist.timestamp).fromNow()

    if (hist.subType == 'end')
      chips.push({
        text: `Retirado ${t}`,
        color: 'error'
      })
    else if (hist.subType == 'start')
      chips.push({
        text: `Instalado ${t}`,
        color: 'info'
      })

    let text = code
    if (extraCode) text += ' ─ ' + extraCode

    items.push({
      value: id,
      chips,
      text
    })
  }

  // Se há veículos de interesse, insere um separador.
  // if (items.length > 0) {
  items.push({ divider: true })
  items.push({
    header: 'Demais veículos'
  })
  // }

  // Demais veículos. Observe que os veículos que já foram
  // inseridos não devem aparecer duas vezes.
  for (let vehicle of allVehicles) {
    let { id, code, extraCode } = vehicle

    if (items.some((x) => x.id == id)) continue

    let chips = []

    let org = orgs.find((y) => y.id == vehicle.org.id)
    if (org) chips.push({ text: org.name })

    if (vehicle.clientId != null)
      chips.push({
        text: `Associado ao módulo ${vehicle.clientId}`,
        color: 'error'
      })

    let text = code
    if (extraCode) text += ' ─ ' + extraCode

    items.push({
      value: id,
      text,
      chips
    })
  }

  return items
}

// Retorna as assocs de um módulo, sendo uma para cada veículo que
// esteve associado. Se existe um registro de assoc/start e depois
// um correspondente assoc/end, traz apenas o assoc/end.
async function fetchAssocsOfInterest(clientId) {
  let assocs = await ClientHistory.findAll({
    clientIds: clientId,
    types: 'association',
    subTypes: 'start,end',
    order: 'timestamp desc, id desc'
  })

  // Agrupa por vehicleId, e pega o registro mais recente
  // de cada um (start ou end)
  let byVid = utils.groupBy(assocs.rows, 'vehicleId')
  let result = []

  for (let value of Object.values(byVid)) {
    let mostRecent = value[0]
    if (mostRecent.vehicleId != null) {
      mostRecent.vehicle = await Vehicle.findById(mostRecent.vehicleId)
      result.push(mostRecent)
    }
  }

  return result
}

// A maioria dos registros (e.g. retirada, troubleshooting, extravio
// etc) se baseia no último registro de INSTALAÇÃO (que pode ser install
// ou remove). Mas para o próprio registro de instalação, nos baseamos
// no último registro de ASSOCIAÇÃO (que pode ser start ou end).
async function findActiveAssoc(clientId) {
  let { rows } = await ClientHistory.findAll({
    clientIds: clientId,
    types: 'association',
    subTypes: 'start,end',
    order: 'timestamp desc, id desc',
    limit: 1
  })

  // Se o registro mais recente for start, retorna esse registro.
  // Se não for, retorna null para indicar que não há assoc ativo.
  if (rows && rows.length && rows[0].subType == 'start') {
    // eslint-disable-next-line no-console
    console.log('has active assoc', rows[0])
    return rows[0]
  } else {
    return null
  }
}

async function findActiveInstall(clientId) {
  let lastInstall = await findLastInstall(clientId)

  // Se o registro mais recente for start, retorna esse registro.
  // Se não for, retorna null para indicar que não há install ativo.
  if (lastInstall && lastInstall.subType == 'install') {
    return lastInstall
  } else {
    return null
  }
}

async function findLastInstall(clientId) {
  let { rows } = await ClientHistory.findAll({
    clientIds: clientId,
    types: 'installation',
    subTypes: 'install,remove',
    order: 'timestamp desc, id desc',
    limit: 1
  })

  let result = rows && rows[0]

  // eslint-disable-next-line no-console
  console.log('Last install record', result)
  return result
}

function canEdit(TheUser) {
  let result = false
  if (
    TheUser &&
    TheUser.user &&
    TheUser.user.profile &&
    TheUser.user.profile.permissions &&
    TheUser.user.profile.permissions.canEditClientHistory
  ) {
    result = true
  }

  // console.log('operation-utils canEdit', TheUser, result)
  return result
}
